import React, { useEffect, useState } from "react";
import { ApiHttpError, fetchSystemStateV2 } from "../api";
import type { SystemStateResponse } from "../types";

const STALE_BARS_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 1 day
const STALE_INDICATORS_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 1 day
// Predictions are flagged stale when they lag the bars they're computed from by
// more than this — a relative check (robust to overnight gaps where bars +
// predictions go stale together) that surfaces "predictions aren't keeping up".
const PREDICTIONS_LAG_STALE_MS = 3 * 60 * 60 * 1000; // 3h behind bars

function num(v: string | number | null | undefined): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function usd(v: string | number | null | undefined): string {
  const n = num(v);
  if (n == null) return "—";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function relativeAge(ts: string | null | undefined, now: number): string {
  if (!ts) return "never";
  const ms = Date.parse(ts);
  if (!Number.isFinite(ms)) return "—";
  const diff = Math.max(0, now - ms);
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}

function ageMs(ts: string | null | undefined, now: number): number | null {
  if (!ts) return null;
  const ms = Date.parse(ts);
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, now - ms);
}

function Timestamp({ ts, now }: { ts: string | null | undefined; now: number }) {
  return (
    <span>
      <span className="mono">{ts ? ts.replace("T", " ").replace("Z", " UTC") : "—"}</span>
      <span className="muted"> ({relativeAge(ts, now)})</span>
    </span>
  );
}

export default function SystemStatePage() {
  const [data, setData] = useState<SystemStateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const now = Date.now();

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fetchSystemStateV2(ctrl.signal)
      .then((payload) => {
        setData(payload);
        setLoading(false);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof ApiHttpError && err.status === 401) {
          window.location.assign("/signin");
          return;
        }
        setError(String((err as Error)?.message || err));
        setLoading(false);
      });
    return () => ctrl.abort();
  }, []);

  if (loading) return <div className="muted">Loading system state…</div>;
  if (error) return <div className="banner error">Failed to load system state: {error}</div>;
  if (!data) return null;

  const p = data.posture;
  const fresh = data.data_freshness;
  const barsAge = ageMs(fresh.bars_latest_ts, now);
  const indAge = ageMs(fresh.indicators_latest_ts, now);
  const barsStale = barsAge == null || barsAge > STALE_BARS_MAX_AGE_MS;
  const indStale = indAge == null || indAge > STALE_INDICATORS_MAX_AGE_MS;
  const barsMs = fresh.bars_latest_ts ? Date.parse(fresh.bars_latest_ts) : null;
  const predsMs = fresh.predictions_latest_ts ? Date.parse(fresh.predictions_latest_ts) : null;
  const predStale =
    predsMs == null || (barsMs != null && barsMs - predsMs > PREDICTIONS_LAG_STALE_MS);
  const posBroker = num(p.open_positions_broker) ?? 0;
  const posSynthetic = num(p.open_positions_synthetic) ?? 0;

  return (
    <section className="view">
      <header className="viewHeader">
        <h2>System State</h2>
        <p className="muted">
          Is anything trading, and is the pipeline alive? As of{" "}
          <span className="mono">{data.as_of_utc || "—"}</span>
        </p>
      </header>

      <div className="kpiGrid">
        <div className="kpi">
          <div className="kpiLabel">Paper accounts</div>
          <div className="kpiValue mono">
            {p.active_paper_accounts}
            <span className="kpiSub"> / {p.paper_accounts} active</span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpiLabel">Enabled strategies</div>
          <div className="kpiValue mono">{p.enabled_strategies}</div>
        </div>
        <div className="kpi">
          <div className="kpiLabel">Open positions</div>
          <div className="kpiValue mono">{p.open_positions}</div>
          <div className="kpiSub muted">
            <span className="badge ok">{posBroker} broker</span>{" "}
            <span className="badge muted">{posSynthetic} synthetic</span>
          </div>
        </div>
      </div>
      <p className="muted hint">
        Only the {posBroker} broker-backed position{posBroker === 1 ? "" : "s"} (Alpaca-paper accounts)
        reconcile with the Alpaca account; synthetic positions are simulated internally and never
        reach a broker.
      </p>

      <h3>Pipeline activity</h3>
      <table className="dataTable">
        <tbody>
          <tr>
            <th>Last decision</th>
            <td><Timestamp ts={p.last_decision_ts} now={now} /></td>
          </tr>
          <tr>
            <th>Last fill</th>
            <td><Timestamp ts={p.last_fill_ts} now={now} /></td>
          </tr>
          <tr>
            <th>Last attribution</th>
            <td><Timestamp ts={p.last_attribution_ts} now={now} /></td>
          </tr>
        </tbody>
      </table>

      <h3>Data freshness</h3>
      <table className="dataTable">
        <thead>
          <tr>
            <th>Source</th>
            <th>Latest timestamp</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr className={barsStale ? "rowStale" : undefined}>
            <th>bars</th>
            <td><Timestamp ts={fresh.bars_latest_ts} now={now} /></td>
            <td>
              {barsStale ? <span className="badge stale">STALE</span> : <span className="badge ok">fresh</span>}
            </td>
          </tr>
          <tr className={indStale ? "rowStale" : undefined}>
            <th>technical_indicators</th>
            <td><Timestamp ts={fresh.indicators_latest_ts} now={now} /></td>
            <td>
              {indStale ? <span className="badge stale">STALE</span> : <span className="badge ok">fresh</span>}
            </td>
          </tr>
          <tr className={predStale ? "rowStale" : undefined}>
            <th>predictions</th>
            <td><Timestamp ts={fresh.predictions_latest_ts} now={now} /></td>
            <td>
              {predStale ? <span className="badge stale">STALE</span> : <span className="badge ok">fresh</span>}
            </td>
          </tr>
        </tbody>
      </table>

      <h3>Paper accounts</h3>
      <table className="dataTable">
        <thead>
          <tr>
            <th>account_key</th>
            <th>broker</th>
            <th>status</th>
            <th>active</th>
            <th className="numCol">initial cash</th>
          </tr>
        </thead>
        <tbody>
          {data.accounts.length === 0 ? (
            <tr>
              <td colSpan={5} className="muted">No paper accounts.</td>
            </tr>
          ) : (
            data.accounts.map((a) => (
              <tr key={a.account_key}>
                <td className="mono">{a.account_key}</td>
                <td>
                  {a.broker === "alpaca_paper" ? (
                    <span className="badge ok">alpaca</span>
                  ) : (
                    <span className="badge muted">synthetic</span>
                  )}
                </td>
                <td>{a.status || "—"}</td>
                <td>
                  {a.active ? <span className="badge ok">active</span> : <span className="badge muted">idle</span>}
                </td>
                <td className="numCol mono">{usd(a.initial_cash_usd)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}
